import { ApplicationResult } from "@/types/generated/UsedGeneratedTypes";
import { Services } from "../services";

type Reference = {
  [key: string]: any;
  rows?: any[];
  localRefName?: string;
  referenceName?: string;
  values?: any;
  localName?: string;
};

type References = {
  [referenceName: string]: {
    [key: string]: Reference;
  };
};
export function dataLoader(services: Services) {
  const dataService = services.dataService;
  const references: References = {};

  const getOrLoadDataByNaturalKey = async (
    application: ApplicationResult,
    referenceName: string,
    key: string,
  ): Promise<Reference | null> => {
    const applicationName = application.configuration.applicationDescription.name;
    let reference: Reference = references?.[referenceName]?.[key];
    if (reference) {
      return reference;
    }
    try {
      reference = await dataService.getDataByNaturalKey(applicationName, referenceName, key);
    }
    catch (_e) {
      return null;
    }
    const rows = reference?.rows ?? [];
    reference = {
      ...reference,
      localRefName: services.internationalisationService.localeReferenceNames(
        referenceName,
        application,
      ),
      referenceName,
      values: rows?.[0],
      localName:
          rows && rows.length !== 0
            ? getValueDisplay(
                rows[0],
                application.configuration.applicationDescription.defaultLanguage,
              )
            : key,
    };
    references[referenceName] = references[referenceName] || {};
    references[referenceName][key] = reference;
    return reference;
  };

  const getDisplayValueForLink = (
    rowValue: string,
    column: { componentKey: string },
    listDisplayRowsValue: Record<string, any>,
    application: ApplicationResult,
  ): string => {
    if (
      listDisplayRowsValue[column.componentKey]
      && Object.keys(listDisplayRowsValue[column.componentKey]).includes(rowValue)
    ) {
      return listDisplayRowsValue[column.componentKey][rowValue].localeName !== rowValue
        ? getValueDisplay(
            listDisplayRowsValue[column.componentKey][rowValue].rows[0],
            application.configuration.applicationDescription.defaultLanguage,
          )
        : listDisplayRowsValue[column.componentKey][rowValue].localName;
    }
    return rowValue;
  };

  const getValueDisplay = (
    referenceLocal: any,
    defaultLanguage?: string,
  ): string => {
    if (referenceLocal?.values !== undefined && referenceLocal?.values !== null) {
      referenceLocal = referenceLocal.values;
      if (!referenceLocal || (typeof referenceLocal === "string" && referenceLocal === "")) {
        return "";
      }
    }
    if (
      referenceLocal?.["__display_" + services.internationalisationService.getLocale()]
      && referenceLocal?.["__display_" + services.internationalisationService.getLocale()] !== null
    ) {
      return referenceLocal["__display_" + services.internationalisationService.getLocale()];
    }
    if (
      defaultLanguage
      && referenceLocal?.["__display_" + defaultLanguage]
      && referenceLocal?.["__display_" + defaultLanguage] !== null
    ) {
      return referenceLocal["__display_" + defaultLanguage];
    }
    else {
      return referenceLocal?.["__display_default"];
    }
  };

  const getColumnNameView = (
    columnId: string,
    application: ApplicationResult,
    dataId: string,
    column?: {
      type: string;
      exportHeader?: string;
      originalName?: string;
    },
  ): string => {
    let columnName: string;
    if (!column) {
      columnName = "this is an error " + columnId;
    }
    else if (column.type === "PatternComponent") {
      columnName = column.originalName || "not found";
    }
    else if (application.data[dataId].componentDescriptions[columnId]) {
      const importHeader = application.data[dataId].componentDescriptions[columnId].importHeader;
      let exportHeader = column?.exportHeader
        ? column.exportHeader
        : application.data[dataId].componentDescriptions[columnId].exportHeaderName;
      exportHeader
        = services.internationalisationService.localeReferenceColumnsNames(
          dataId,
          exportHeader,
          application,
        ) || exportHeader;
      if (exportHeader !== columnId) {
        columnName = exportHeader || columnId;
      }
      else if (importHeader !== undefined && importHeader !== columnId) {
        columnName = importHeader;
      }
      else if (columnId.match(".*::.*")) {
        columnName = services.internationalisationService.localeReferenceColumnsNames(
          dataId,
          columnId,
          application,
        );
      }
      else {
        columnName = columnId;
      }
    }
    else if (columnId.match("(.*::.*)::.*")) {
      const match = columnId.match("(.*::.*)::.*");
      columnName = services.internationalisationService.localeReferenceColumnsNames(
        dataId,
        match ? match[1] : columnId,
        application,
      );
    }
    else if (columnId.match(".*::.*")) {
      columnName = services.internationalisationService.localeReferenceColumnsNames(
        dataId,
        columnId,
        application,
      );
    }
    else {
      columnName = columnId;
    }
    if (columnName.match(".*::.*")) {
      const nameMatch = columnId.match("(.*)::(.*)");
      if (nameMatch && nameMatch[2]) {
        columnName = nameMatch[2];
      }
    }
    return columnName;
  };
  return {
    getOrLoadDataByNaturalKey,
    getValueDisplay,
    getColumnNameView,
    getDisplayValueForLink,
  };
}
